import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { langClass } from "@/i18n/langUtils";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, KeyRound, User as UserIcon, Loader2, ShieldCheck, GraduationCap, BarChart3, Check } from "lucide-react";
import { buildGoogleAuthUrl, isGoogleAuthConfigured } from "@/lib/googleAuth";

const Register = () => {
  const { t, lang } = useT();
  const auth = t.auth || {};
  const { register } = useAuth();
  const nav = useNavigate();
  const langCls = langClass(lang);

  const ROLES = useMemo(
    () => [
      { key: "user", title: auth.roleUser, desc: auth.roleUserDesc, icon: UserIcon },
      { key: "mentor", title: auth.roleMentor, desc: auth.roleMentorDesc, icon: GraduationCap },
      { key: "advisory", title: auth.roleAdvisory, desc: auth.roleAdvisoryDesc, icon: BarChart3 },
    ],
    [
      auth.roleUser,
      auth.roleUserDesc,
      auth.roleMentor,
      auth.roleMentorDesc,
      auth.roleAdvisory,
      auth.roleAdvisoryDesc,
    ],
  );
  const [role, setRole] = useState("user");
  const [googleLoginUrl, setGoogleLoginUrl] = useState(() => buildGoogleAuthUrl());

  useEffect(() => {
    setGoogleLoginUrl(buildGoogleAuthUrl());
  }, []);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError(auth.passwordMin);
      return;
    }
    setLoading(true);
    try {
      await register(form.email.trim(), form.password, form.name.trim(), role);
      nav("/dashboard", { replace: true });
    } catch (err) {
      // surface the real error message rather than a generic one
      setError(formatApiError(err) || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 md:py-20" data-testid="register-page">
      <div className="text-center">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]/30 mb-4">
          <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
        </div>
        <h1 className={`font-display text-3xl font-black ${langCls}`}>{auth.joinTitle}</h1>
        <p className={`mt-2 text-sm text-white/60 ${langCls}`}>{auth.joinSubtitleShort || auth.joinSubtitle}</p>
      </div>

      {/* Role picker */}
      <div className="mt-8">
        <div className={`text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt mb-3 text-center ${langCls}`}>{auth.signingUpAs}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="role-picker">
          {ROLES.map((r) => {
            const Icon = r.icon;
            const active = role === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => setRole(r.key)}
                className={`relative text-left p-4 rounded-xl border transition-all ${
                  active
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/25"
                }`}
                data-testid={`role-${r.key}`}
              >
                {active && <Check className="absolute top-3 right-3 h-4 w-4 text-[#D4AF37]" />}
                <Icon className={`h-5 w-5 ${active ? "text-[#D4AF37]" : "text-white/55"}`} />
                <div className={`mt-2 font-display font-bold ${active ? "text-white" : "text-white/85"}`}>{r.title}</div>
                <div className="mt-1 text-[11px] text-white/55 leading-snug">{r.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {googleLoginUrl && isGoogleAuthConfigured() ? (
        <a href={googleLoginUrl} className="block mt-8" data-testid="google-register-btn">
        <Button type="button" variant="outline" className="w-full h-11 border-white/20 hover:bg-white/5 gap-3">
          <svg viewBox="0 0 24 24" className="h-4 w-4">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          {auth.continueGoogle}
        </Button>
      </a>
      ) : (
        <p className="mt-8 text-xs text-amber-200/90 text-center border border-amber-500/30 rounded-lg px-3 py-2 bg-amber-500/10">
          Google sign-up is not in this build. Set <code className="text-amber-100">REACT_APP_GOOGLE_CLIENT_ID</code> for
          Production in Cloudflare, then Retry deployment.
        </p>
      )}

      <div className="my-6 flex items-center gap-3 text-xs text-white/40 font-mono-alt uppercase tracking-widest">
        <span className="flex-1 h-px bg-white/10" /> {auth.orEmail} <span className="flex-1 h-px bg-white/10" />
      </div>

      <form onSubmit={submit} className="surface-card p-6 space-y-4" data-testid="register-form">
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{auth.name}</Label>
          <div className="relative mt-2">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input value={form.name} onChange={set("name")} required className={`pl-10 ${fieldCls}`} data-testid="reg-name" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{auth.email}</Label>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input value={form.email} onChange={set("email")} type="email" required className={`pl-10 ${fieldCls}`} data-testid="reg-email" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{auth.password}</Label>
          <div className="relative mt-2">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input value={form.password} onChange={set("password")} type="password" required minLength={6} className={`pl-10 ${fieldCls}`} data-testid="reg-password" />
          </div>
          <div className="mt-1 text-[11px] text-white/40">{auth.minPassword}</div>
        </div>
        {error && <div className="text-xs text-[#FF6A60]" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="register-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `${auth.signUp} — ${ROLES.find((r) => r.key === role)?.title || ""}`}
        </Button>
      </form>

      <div className={`mt-6 text-center text-sm text-white/60 ${langCls}`}>
        {auth.haveAccount}{" "}
        <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="goto-login">{auth.gotoSignIn}</Link>
      </div>
    </div>
  );
};

export default Register;
